import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../common/state/store";
import {
  getConnectedDevice,
  getInstallJob,
  getPackageScanStatus,
} from "../state/deviceInstallerSlice";
import { scanInstalledPackages } from "../utils/packageScanner";
import { POLL_INTERVAL_MS } from "../constants";

/**
 * Periodically re-scans installed packages on the connected device so
 * the UI stays in sync when the user installs/uninstalls apps directly
 * on the device (outside the web interface).
 *
 * Polling is suspended while an install job is active to avoid concurrent
 * ADB shell commands that can block the transport on some devices.
 */
export function usePackagePolling() {
  const dispatch: AppDispatch = useDispatch();
  const device = useSelector(getConnectedDevice);
  const scanStatus = useSelector(getPackageScanStatus);
  const installJob = useSelector(getInstallJob);
  const scanStatusRef = useRef(scanStatus);
  scanStatusRef.current = scanStatus;

  const isReady = device?.status === "ready";
  const isInstalling =
    installJob != null && installJob.status !== "success" && installJob.status !== "error";

  useEffect(() => {
    if (!isReady || isInstalling) return undefined;

    const id = setInterval(() => {
      if (scanStatusRef.current === "scanning") return;

      scanInstalledPackages(dispatch).catch(() => {
        // Non-blocking — don't break the polling loop
      });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isReady, isInstalling, dispatch]);
}
